import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { toast } from '../../lib/shadcn/sonner'
import { useUpdateIngredientsBulk } from '../../hooks/backend/recipes'
import { useGetUnits } from '../../hooks/backend/settingsPage'
import type { Unit } from '../../utils/units'
import { formatUnitCost } from '../../utils/format'
import UnitSelect from './UnitSelect'
import type { IngredientWithComponents } from './IngredientFormDialog'
import type { IngredientCategory } from './IngredientCategoriesDialog'

const UNCATEGORIZED = '__none'
/** Unit picker value meaning "leave each ingredient's own unit". */
const KEEP_UNIT = ''

interface IngredientBulkEditDialogProps {
  ingredients: IngredientWithComponents[]
  categories: IngredientCategory[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => Promise<unknown> | void
}

export default function IngredientBulkEditDialog({
  ingredients,
  categories,
  open,
  onOpenChange,
  onSaved,
}: IngredientBulkEditDialogProps) {
  const updateBulk = useUpdateIngredientsBulk()
  const getUnits = useGetUnits()
  const units = (getUnits.data as Unit[] | undefined) ?? []

  const [applyCategory, setApplyCategory] = useState(false)
  const [categoryId, setCategoryId] = useState('')
  const [applyCost, setApplyCost] = useState(false)
  const [cost, setCost] = useState('')
  const [costQty, setCostQty] = useState('1')
  const [costUnit, setCostUnit] = useState(KEEP_UNIT)

  useEffect(() => {
    if (open) {
      setApplyCategory(false)
      setCategoryId('')
      setApplyCost(false)
      setCost('')
      setCostQty('1')
      // Start with the unit they share, if they all use the same one.
      const shared = new Set(ingredients.map(i => i.cost_unit ?? ''))
      setCostUnit(shared.size === 1 ? ([...shared][0] ?? KEEP_UNIT) : KEEP_UNIT)
      void getUnits.trigger({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const n = ingredients.length
  const mixedUnits = new Set(ingredients.map(i => i.cost_unit ?? '')).size > 1
  const costNum = cost.trim() === '' ? null : Number(cost)
  const qtyNum = costQty.trim() === '' ? 1 : Number(costQty)
  const unitCost = costNum != null && !Number.isNaN(costNum) && qtyNum > 0 ? costNum / qtyNum : null

  const handleSave = async () => {
    if (n === 0) return
    if (!applyCategory && !applyCost) {
      toast.error('Tick at least one field to change')
      return
    }
    const params: {
      ids: string[]
      category_id?: string | null
      cost?: number | null
      cost_qty?: number | null
      cost_unit?: string
    } = { ids: ingredients.map(i => i.id) }
    if (applyCategory) {
      if (!categoryId) {
        toast.error('Pick a category')
        return
      }
      params.category_id = categoryId === UNCATEGORIZED ? null : categoryId
    }
    if (applyCost) {
      if (costNum != null && (Number.isNaN(costNum) || costNum < 0)) {
        toast.error('Cost must be a valid non-negative number')
        return
      }
      if (Number.isNaN(qtyNum) || qtyNum <= 0) {
        toast.error('Quantity must be a number greater than 0')
        return
      }
      params.cost = costNum == null ? null : costNum / qtyNum
      params.cost_qty = qtyNum === 1 ? null : qtyNum
      if (costUnit !== KEEP_UNIT) params.cost_unit = costUnit
    }
    try {
      const res = (await updateBulk.trigger(params)) as { updated?: number } | undefined
      const count = res?.updated ?? n
      toast.success(`Updated ${count} ingredient${count === 1 ? '' : 's'}`)
      onOpenChange(false)
      await onSaved()
    } catch (error) {
      toast.error(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const names = ingredients.map(i => i.name)
  const shownNames = names.slice(0, 8).join(', ') + (names.length > 8 ? `, and ${names.length - 8} more` : '')

  return (
    <Dialog open={open} onOpenChange={next => { if (!updateBulk.loading) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Edit Ingredients</DialogTitle>
          <DialogDescription>
            Tick a field to apply it to all {n} selected ingredient{n === 1 ? '' : 's'}. Fields left
            unticked aren't changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 flex-1 overflow-y-auto min-h-0">
          <p className="text-xs text-muted-foreground">{shownNames}</p>

          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-ingredient-category"
              checked={applyCategory}
              onCheckedChange={v => setApplyCategory(!!v)}
              className="mt-0.5"
            />
            <div className="flex-1 space-y-2">
              <Label htmlFor="bulk-ingredient-category" className="text-sm font-medium">
                Category
              </Label>
              <Select
                value={categoryId}
                onValueChange={v => { setCategoryId(v); setApplyCategory(true) }}
              >
                <SelectTrigger aria-label="Category for selected ingredients">
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                  {categories.length > 0 ? <SelectSeparator /> : null}
                  <SelectItem value={UNCATEGORIZED}>Uncategorized</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="bulk-ingredient-cost"
              checked={applyCost}
              onCheckedChange={v => setApplyCost(!!v)}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0 space-y-2">
              <Label htmlFor="bulk-ingredient-cost" className="text-sm font-medium">
                Cost
              </Label>
              <div className="grid grid-cols-[1fr_auto_4.5rem] items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={cost}
                  onChange={e => { setCost(e.target.value); setApplyCost(true) }}
                  placeholder="e.g. 28.80"
                  aria-label="Cost"
                />
                <span className="text-sm text-muted-foreground">for</span>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={costQty}
                  onChange={e => { setCostQty(e.target.value); setApplyCost(true) }}
                  placeholder="1"
                  aria-label="Quantity"
                />
              </div>
              <UnitSelect
                key={costUnit === KEEP_UNIT ? 'keep' : 'set'}
                value={costUnit}
                onChange={u => { setCostUnit(u); setApplyCost(true) }}
                units={units}
                placeholder="Keep each ingredient's unit"
              />
              {costUnit !== KEEP_UNIT ? (
                <button
                  type="button"
                  onClick={() => setCostUnit(KEEP_UNIT)}
                  className="text-xs text-primary hover:underline"
                >
                  Keep each ingredient's own unit instead
                </button>
              ) : null}
              {unitCost != null && qtyNum !== 1 ? (
                <p className="text-xs">
                  <span className="text-muted-foreground">Works out to </span>
                  <span className="font-medium tabular-nums">
                    {formatUnitCost(unitCost)}
                    {costUnit ? ` per ${costUnit}` : ' per unit'}
                  </span>
                </p>
              ) : null}
              {costUnit === KEEP_UNIT && mixedUnits ? (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  These ingredients use different units, so the same price will apply per each
                  one's own unit. Pick a unit to set them all the same.
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Leave the price blank to clear the cost. Recipe and product costs update
                to match.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updateBulk.loading}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={updateBulk.loading || (!applyCategory && !applyCost)}
          >
            {updateBulk.loading ? 'Saving…' : `Update ${n}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
